import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";

/**
 * Keyboard shortcuts (DECISIONS #53). Keys are single characters ("j", "?"), key names
 * ("Enter", "Escape", "ArrowDown"), or "mod+k" for Ctrl+K / Cmd+K.
 *
 * Shortcuts never fire while someone is typing in a field (except mod+ keys, like Ctrl+K), or
 * while a dialog or the chat drawer has the focus, so they can't get in the way.
 */
export type Hotkeys = Record<string, (event: KeyboardEvent) => void>;

function typing(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

function keyOf(event: KeyboardEvent): string {
  const mod = event.ctrlKey || event.metaKey;
  const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
  return mod ? `mod+${key}` : key;
}

export function useHotkeys(bindings: Hotkeys, enabled = true): void {
  // The latest handlers without re-adding the listener on every render.
  const current = useRef(bindings);
  useEffect(() => {
    current.current = bindings;
  });
  useEffect(() => {
    if (!enabled) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.defaultPrevented || event.isComposing || event.altKey) return;
      const key = keyOf(event);
      const handler = current.current[key];
      if (!handler) return;
      const modified = key.startsWith("mod+");
      if (!modified && (event.ctrlKey || event.metaKey)) return;
      if (!modified && typing(event.target)) return;
      // Enter on a link or button does what it always does.
      if (key === "Enter" && event.target instanceof HTMLElement && event.target.closest("a, button, [role=button]")) return;
      // A dialog (or the chat drawer) on top owns the keyboard.
      if (document.querySelector("dialog[open]")) return;
      if (document.activeElement?.closest("[role=dialog]")) return;
      event.preventDefault();
      handler(event);
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [enabled]);
}

/** Whether the screen matches a media query, kept up to date (false where unsupported). */
export function useMediaQuery(query: string): boolean {
  const supported = typeof window !== "undefined" && typeof window.matchMedia === "function";
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!supported) return () => undefined;
      const list = window.matchMedia(query);
      list.addEventListener("change", onChange);
      return () => list.removeEventListener("change", onChange);
    },
    [query, supported],
  );
  return useSyncExternalStore(subscribe, () => (supported ? window.matchMedia(query).matches : false));
}
